import React, { useState, useRef, useEffect, useMemo } from "react";
import { apiFetch } from '@/lib/api';
import { nanoid } from "nanoid";
import { useEditor } from "./store";
import useFileUpload from "@/hooks/useFileUpload";
import { Input } from "@/components/ui/shadcn/input";
import SafeImage from "@/components/ui/SafeImage";
import { useDragPreview } from "@/components/ui/DragPreview";
import {
  FiFolder,
  FiChevronRight,
  FiChevronLeft,
  FiUpload,
  FiZoomIn,
  FiZoomOut,
  FiPlus,
  FiDownload,
} from "react-icons/fi";
import { HiOutlineFolderPlus } from "react-icons/hi2";
import { LuAudioLines, LuVideo, LuImage, LuFile, LuScrollText, LuFolder, LuFolderLock } from "react-icons/lu";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/shadcn/dialog";
import AssetsBrowser from "@/components/AssetsBrowser";
import { useModal } from "@/contexts/ModalContext";
import ImagePreviewModal from "@/components/modals/ImagePreviewModal";
import VideoPreviewModal from "@/components/modals/VideoPreviewModal";
import AudioPreviewModal from "@/components/modals/AudioPreviewModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import { sanitizeFileName } from "@/lib/fileNames";

const MEDIA_TYPE_ICONS = {
  image: LuImage,
  video: LuVideo,
  audio: LuAudioLines,
  script: LuScrollText,
  none: LuFile,
};

const SCRIPT_FOLDER_ID = "__script_folder__";
const STORYBOARD_FOLDER_ID = "__storyboard_folder__";
const STORYBOARD_PANEL_PREFIX = "__storyboard_panel__";
const STORYBOARD_VISUAL_TYPES = new Set(["asset", "generation", "url", "sketch"]);

function GridItem({ item, onClick, onDoubleClick, onRename, onDragStart, editingId, setEditingId, isSelected }) {
  const [editName, setEditName] = useState(item.name);
  const isEditing = editingId === item.id;
  const isFolder = item.type === "folder";

  const handleRenameSubmit = () => {
    if (editName.trim() && editName.trim() !== item.name) {
      onRename(item.id, editName.trim());
    }
    setEditingId(null);
  };

  return (
    <div
      className={`flex flex-col items-start gap-1 p-1 rounded-md hover:bg-secondary/40 group cursor-default relative ${isSelected ? "ring-[1.5px] ring-primary bg-primary/10" : ""}`}
      draggable={!isFolder}
      onClick={(e) => {
        e.stopPropagation();
        if (!isEditing) onClick(item, e);
      }}
      onDragStart={(e) => {
        if (!isFolder) onDragStart(e, item);
      }}
      onDoubleClick={(e) => {
        if (isEditing) return;
        if (isFolder) {
          onDoubleClick(item.id);
        }
      }}
    >
      {/* Thumbnail / Icon */}
      <div
        className="w-full aspect-video rounded-md overflow-hidden flex items-center justify-center bg-secondary/40 relative cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          if (isEditing) return;
          if (!isFolder) onClick(item, e);
        }}
        onDoubleClick={(e) => {
          if (isEditing) return;
          if (!isFolder) {
            e.stopPropagation();
            onDoubleClick(item);
          }
        }}
      >
        {isFolder ? (
          item.canDelete ? (
            <LuFolder className="w-1/3 h-1/3 min-w-4 min-h-4 text-secondary-foreground/60" />
          ) : (
            <LuFolderLock className="w-1/3 h-1/3 min-w-4 min-h-4 text-secondary-foreground/60" />
          )
        ) : item.thumbnailUrl ? (
          <div className="w-full h-full relative">
            <SafeImage src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          (() => {
            const Icon = item.type === "script" ? LuAudioLines : (MEDIA_TYPE_ICONS[item.mediaType] || LuFile);
            return <Icon className="w-1/3 h-1/3 min-w-4 min-h-4 text-secondary-foreground/40" />;
          })()
        )}
        {!isFolder && item.mediaType && item.mediaType !== "none" && (() => {
          const Icon = MEDIA_TYPE_ICONS[item.mediaType];
          return Icon ? (
            <div className="absolute bottom-0.5 left-0.5 bg-black/60 rounded p-0.5 w-1/8 w-full max-w-5 aspect-square select-none">
              <Icon className="w-full h-full text-secondary-foreground" />
            </div>
          ) : null;
        })()}
        {!isFolder && item.url && (
          <button
            className="absolute bottom-0.5 right-0.5 bg-black/60 hover:bg-black/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            onClick={async (e) => {
              e.stopPropagation();
              e.preventDefault();
              try {
                const res = await fetch(item.url);
                const blob = await res.blob();
                const ext = item.url.match(/\.(\w+)(?:\?|$)/)?.[1] || "";
                const filename = ext ? `${item.name || "download"}.${ext}` : (item.name || "download");
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = sanitizeFileName(filename, "download");
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (err) {
                console.error("Download failed:", err);
              }
            }}
            title="Download"
          >
            <FiDownload className="w-3.5 h-3.5 text-secondary-foreground" />
          </button>
        )}
      </div>

      {/* Name */}
      {isEditing ? (
        <form
          className="w-full"
          onSubmit={(e) => {
            e.preventDefault();
            handleRenameSubmit();
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="h-5 text-xs py-0 px-1"
            autoFocus
            onBlur={handleRenameSubmit}
            onKeyDown={(e) => {
              if (e.key === "Escape") setEditingId(null);
            }}
          />
        </form>
      ) : (
        <span
          className="text-xs text-secondary-foreground truncate w-full leading-tight select-none"
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (!item.canDelete) return;
            setEditingId(item.id);
            setEditName(item.name);
          }}
          title={item.name}
        >
          {item.name}
        </span>
      )}
    </div>
  );
}

export default function TimelineAssets() {
  const assetsFolder = useEditor((s) => s.assetsFolder);
  const addAssetFolderItem = useEditor((s) => s.addAssetFolderItem);
  const removeAssetFolderItem = useEditor((s) => s.removeAssetFolderItem);
  const removeAssetFolderItemWithClips = useEditor((s) => s.removeAssetFolderItemWithClips);
  const updateAssetFolderItem = useEditor((s) => s.updateAssetFolderItem);
  const showId = useEditor((s) => s.showId);
  const episodeId = useEditor((s) => s.episodeId);
  const segmentId = useEditor((s) => s.segmentId);
  const scriptItems = useEditor((s) => s.scriptItems);
  const setScriptItems = useEditor((s) => s.setScriptItems);
  const storyboardItems = useEditor((s) => s.storyboardItems);
  const setStoryboardItems = useEditor((s) => s.setStoryboardItems);
  const { uploadFile, uploadProgress, isUploading } = useFileUpload();
  const { openModal } = useModal();
  const { showDragPreview } = useDragPreview();
  const fileInputRef = useRef(null);

  const currentFolderId = useEditor((s) => s.currentAssetFolderId);
  const setCurrentFolderId = useEditor((s) => s.setCurrentAssetFolderId);

  const [editingId, setEditingId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [columns, setColumns] = useState(2);
  const [showAssetsBrowser, setShowAssetsBrowser] = useState(false);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [batchUpload, setBatchUpload] = useState(null);

  useEffect(() => {
    console.log("Script folder effect:", { showId, episodeId, segmentId });
    if (!showId || !episodeId || !segmentId) {
      setScriptItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/shows/${showId}/episodes/${episodeId}/segments/${segmentId}/scenes`);
        if (!res.ok) {
          console.error("Failed to fetch scenes:", res.status, await res.text());
          return;
        }
        const scenes = await res.json();
        console.log("Fetched scenes for script folder:", scenes.length, "scenes", scenes);
        const items = [];
        for (const scene of scenes) {
          if (!scene.script) continue;
          for (const script of scene.script) {
            if (!script.character) continue;
            items.push({
              id: `script_${script._id}`,
              parentId: SCRIPT_FOLDER_ID,
              type: "script",
              mediaType: "audio",
              name: script.speech || script.text || "Untitled",
              canDelete: false,
              url: script.audio_url || null,
              thumbnailUrl: null,
              duration: script.audio_duration || null,
              scriptId: script._id,
              sceneId: scene._id,
              characterId: script.character?._id || null,
              speech: script.speech || "",
            });
          }
        }
        if (!cancelled) setScriptItems(items);
      } catch (e) {
        console.error("Failed to fetch segment scripts:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [showId, episodeId, segmentId]);

  useEffect(() => {
    if (!showId || !episodeId || !segmentId) {
      setStoryboardItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/shows/${showId}/episodes/${episodeId}/segments/${segmentId}/storyboard`);
        if (!res.ok) return;
        const storyboard = await res.json();
        const items = [];
        for (const element of storyboard.elements || []) {
          const panelId = `${STORYBOARD_PANEL_PREFIX}${element._id}`;
          items.push({
            id: panelId,
            parentId: STORYBOARD_FOLDER_ID,
            type: "folder",
            mediaType: "none",
            name: element.name || "Untitled",
            canDelete: false,
          });
          for (const item of element.items || []) {
            if (!STORYBOARD_VISUAL_TYPES.has(item.type)) continue;
            const assetUrl = item.sketchCompositeUrl || item.asset?.url || item.url || null;
            const mediaType = item.asset?.mediaType || item.mediaType || "image";
            const thumbUrl = item.asset?.thumbnail_url || (mediaType === "image" ? assetUrl : null);
            if (!assetUrl) continue;
            items.push({
              id: `storyboard_${item._id}`,
              parentId: panelId,
              type: "storyboard",
              mediaType,
              name: item.name || item.asset?.name || item.type ||  "Untitled",
              canDelete: false,
              url: assetUrl,
              thumbnailUrl: thumbUrl,
              duration: null,
            });
          }
        }
        if (!cancelled) setStoryboardItems(items);
      } catch (e) {
        console.error("Failed to fetch storyboard:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [showId, episodeId, segmentId]);

  const scriptFolder = segmentId ? {
    id: SCRIPT_FOLDER_ID,
    parentId: null,
    type: "folder",
    mediaType: "none",
    name: "Script",
    canDelete: false,
  } : null;

  const storyboardFolder = segmentId ? {
    id: STORYBOARD_FOLDER_ID,
    parentId: null,
    type: "folder",
    mediaType: "none",
    name: "Storyboard",
    canDelete: false,
  } : null;

  const allItems = useMemo(() => {
    if (currentFolderId === SCRIPT_FOLDER_ID) {
      return scriptItems;
    }
    if (currentFolderId === STORYBOARD_FOLDER_ID) {
      return storyboardItems.filter((i) => i.parentId === STORYBOARD_FOLDER_ID);
    }
    if (currentFolderId?.startsWith(STORYBOARD_PANEL_PREFIX)) {
      return storyboardItems.filter((i) => i.parentId === currentFolderId);
    }
    const currentChildren = assetsFolder.filter((i) => i.parentId === currentFolderId);
    const folders = currentChildren.filter((i) => i.type === "folder");
    const files = currentChildren.filter((i) => i.type !== "folder");
    const result = [...folders, ...files];
    if (!currentFolderId) {
      if (storyboardFolder) result.unshift(storyboardFolder);
      if (scriptFolder) result.unshift(scriptFolder);
    }
    return result;
  }, [assetsFolder, currentFolderId, scriptFolder, storyboardFolder, scriptItems, storyboardItems]);

  const items = allItems;

  const handleNavigateInto = (folderId) => {
    setCurrentFolderId(folderId);
    setSelectedAssetIds([]);
  };

  const handleNavigateUp = () => {
    if (!currentFolderId) return;
    setSelectedAssetIds([]);
    if (currentFolderId === SCRIPT_FOLDER_ID || currentFolderId === STORYBOARD_FOLDER_ID) {
      setCurrentFolderId(null);
      return;
    }
    if (currentFolderId?.startsWith(STORYBOARD_PANEL_PREFIX)) {
      setCurrentFolderId(STORYBOARD_FOLDER_ID);
      return;
    }
    const currentFolder = assetsFolder.find((i) => i.id === currentFolderId);
    setCurrentFolderId(currentFolder?.parentId || null);
  };

  const handleCreateFolder = () => {
    const id = nanoid();
    addAssetFolderItem({
      id,
      parentId: currentFolderId,
      type: "folder",
      mediaType: "none",
      name: "New Folder",
      canDelete: true,
    });
    setEditingId(id);
  };

  const handleDelete = async (itemId) => {
    const descendants = useEditor.getState().getAssetFolderDescendants(itemId);
    const affectedClips = useEditor.getState().getClipsForAssetFolderItems(descendants);

    if (affectedClips.length > 0) {
      const clipCount = affectedClips.length;
      const confirmed = await openModal(
        <ConfirmModal
          title="Delete Asset"
          message={`This will also remove ${clipCount} clip${clipCount > 1 ? "s" : ""} from the timeline. Are you sure?`}
        />
      );
      if (!confirmed) return;
      removeAssetFolderItemWithClips(itemId);
    } else {
      removeAssetFolderItem(itemId);
    }
  };

  const handleDeleteSelected = async () => {
    const deletableIds = selectedAssetIds.filter((id) => {
      const item = items.find((i) => i.id === id);
      return item?.canDelete;
    });
    if (deletableIds.length === 0) return;

    let totalAffectedClips = [];
    for (const id of deletableIds) {
      const descendants = useEditor.getState().getAssetFolderDescendants(id);
      const clips = useEditor.getState().getClipsForAssetFolderItems(descendants);
      totalAffectedClips.push(...clips);
    }

    if (totalAffectedClips.length > 0) {
      const clipCount = totalAffectedClips.length;
      const confirmed = await openModal(
        <ConfirmModal
          title="Delete Assets"
          message={`This will also remove ${clipCount} clip${clipCount > 1 ? "s" : ""} from the timeline. \nThis action cannot be undone. Are you sure?`}
        />
      );
      if (!confirmed) return;
      for (const id of deletableIds) removeAssetFolderItemWithClips(id);
    } else {
      for (const id of deletableIds) removeAssetFolderItem(id);
    }
    setSelectedAssetIds([]);
  };

  const handleKeyDown = (e) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      e.stopPropagation();
      if (selectedAssetIds.length > 0) handleDeleteSelected();
    }
  };

  const handleRename = (itemId, newName) => {
    updateAssetFolderItem(itemId, { name: newName });
  };

  const handleImportFiles = () => {
    fileInputRef.current?.click();
  };

  const processFileUpload = async (file) => {
    const result = await uploadFile(file, "timeline-assets");
    if (!result) return;

    const id = nanoid();
    addAssetFolderItem({
      id,
      parentId: currentFolderId,
      type: "upload",
      mediaType: result.mediaType,
      name: file.name.replace(/\.[^/.]+$/, ""),
      canDelete: true,
      url: result.url,
      thumbnailUrl: result.thumbnail_url || null,
      duration: null,
    });
  };

  const uploadFiles = async (files) => {
    if (files.length === 0) return;
    setBatchUpload({ total: files.length, completed: 0 });
    for (let i = 0; i < files.length; i++) {
      setBatchUpload({ total: files.length, completed: i });
      await processFileUpload(files[i]);
    }
    setBatchUpload(null);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    await uploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const assetData = e.dataTransfer.getData("application/timeline-asset");
    if (assetData) {
      try {
        const item = JSON.parse(assetData);
        if (!item.url) return;
        const { assetsFolder: currentFolder } = useEditor.getState();
        if (currentFolder.some((i) => i.url === item.url)) return;
        const id = nanoid();
        addAssetFolderItem({
          id,
          parentId: currentFolderId,
          type: "generation",
          mediaType: item.mediaType || "none",
          name: item.name || "Untitled",
          canDelete: true,
          url: item.url,
          thumbnailUrl: item.thumbnailUrl || null,
          duration: null,
        });
      } catch {
        // ignore malformed data
      }
      return;
    }

    if (e.dataTransfer.files?.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      await uploadFiles(droppedFiles);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleAssetDragStart = (e, item) => {
    showDragPreview(e, { thumbnailUrl: item.thumbnailUrl, name: item.name, mediaType: item.mediaType });
    e.dataTransfer.setData(
      "application/timeline-asset",
      JSON.stringify(item)
    );
    const isAudio = item.mediaType === "audio" || item.type === "script";
    e.dataTransfer.setData(
      isAudio ? "application/timeline-asset-audio" : "application/timeline-asset-visual",
      ""
    );
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleBrowserSelect = (assetsOrAsset) => {
    const assets = Array.isArray(assetsOrAsset) ? assetsOrAsset : [assetsOrAsset];
    for (const asset of assets) {
      const id = nanoid();
      addAssetFolderItem({
        id,
        parentId: currentFolderId,
        type: "kagami",
        mediaType: asset.mediaType || "none",
        name: asset.name || "Untitled",
        canDelete: true,
        url: asset.url || null,
        thumbnailUrl: asset.thumbnail_url || null,
        duration: null,
      });
    }
    setShowAssetsBrowser(false);
  };

  const handleSelect = (item, e) => {
    const isMulti = e.ctrlKey || e.metaKey;
    setSelectedAssetIds((prev) => {
      if (isMulti) {
        return prev.includes(item.id)
          ? prev.filter((id) => id !== item.id)
          : [...prev, item.id];
      }
      return prev.length === 1 && prev[0] === item.id ? [] : [item.id];
    });
  };

  const handlePreview = (item) => {
    const asset = { url: item.url, thumbnail_url: item.thumbnailUrl, name: item.name, mediaType: item.mediaType };
    if (item.mediaType === "video") {
      openModal(<VideoPreviewModal video={asset} />);
    } else if (item.mediaType === "audio") {
      openModal(<AudioPreviewModal audio={asset} />);
    } else if (item.mediaType === "image") {
      openModal(<ImagePreviewModal image={asset} />);
    }
  };

  // Breadcrumb path
  const getBreadcrumbPath = () => {
    if (!currentFolderId) return [];
    if (currentFolderId === SCRIPT_FOLDER_ID) {
      return [scriptFolder];
    }
    if (currentFolderId === STORYBOARD_FOLDER_ID) {
      return [storyboardFolder];
    }
    if (currentFolderId?.startsWith(STORYBOARD_PANEL_PREFIX)) {
      const panelFolder = storyboardItems.find((i) => i.id === currentFolderId);
      return [storyboardFolder, panelFolder].filter(Boolean);
    }
    const path = [];
    let id = currentFolderId;
    while (id) {
      const folder = assetsFolder.find((i) => i.id === id);
      if (!folder) break;
      path.unshift(folder);
      id = folder.parentId;
    }
    return path;
  };

  const breadcrumbs = getBreadcrumbPath();
  const currentFolderName = currentFolderId
    ? (currentFolderId === SCRIPT_FOLDER_ID
        ? "Script"
        : currentFolderId === STORYBOARD_FOLDER_ID
        ? "Storyboard"
        : currentFolderId?.startsWith(STORYBOARD_PANEL_PREFIX)
        ? storyboardItems.find((i) => i.id === currentFolderId)?.name || "Panel"
        : assetsFolder.find((i) => i.id === currentFolderId)?.name || "Folder")
    : null;

  const isLockedFolder = currentFolderId === SCRIPT_FOLDER_ID ||
    currentFolderId === STORYBOARD_FOLDER_ID ||
    currentFolderId?.startsWith(STORYBOARD_PANEL_PREFIX) ||
    (currentFolderId && assetsFolder.find((i) => i.id === currentFolderId)?.canDelete === false);

  return (
    <div
      className="w-full h-full bg-background relative outline-none"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-timeline-assets
    >
      <div className="absolute inset-0 flex flex-col">
        {/* Header — matches TimeRuler height (28px) */}
        <div className="flex items-center justify-between px-3 border-b flex-shrink-0" style={{ height: 28 }}>
          {/* <span className="text-[10px] uppercase text-secondary-foreground/80 select-none">
            Assets
          </span> */}
          <div className="flex items-center gap-1">
            <button
              className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
              onClick={() => setShowAssetsBrowser(true)}
              disabled={isLockedFolder}
              title="Add From Assets"
            >
              <FiPlus className="w-3.5 h-3.5" />
            </button>
            <button
              className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
              onClick={handleImportFiles}
              disabled={isLockedFolder}
              title="Upload Files"
            >
              <FiUpload className="w-3.5 h-3.5" />
            </button>
            <button
              className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
              onClick={handleCreateFolder}
              disabled={isLockedFolder}
              title="New Folder"
            >
              <HiOutlineFolderPlus className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
              onClick={() => setColumns((c) => Math.min(c + 1, 5))}
              disabled={columns >= 5}
              title="Zoom out"
            >
              <FiZoomOut className="w-3.5 h-3.5" />
            </button>
            <button
              className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
              onClick={() => setColumns((c) => Math.max(c - 1, 1))}
              disabled={columns <= 1}
              title="Zoom in"
            >
              <FiZoomIn className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Breadcrumb navigation */}
        {currentFolderId && (
          <div className="flex items-center gap-1 px-2 py-1.5 border-b text-xs text-secondary-foreground flex-shrink-0">
            <button
              className="p-0.5 hover:bg-secondary rounded cursor-pointer flex-shrink-0"
              onClick={handleNavigateUp}
              title="Go back"
            >
              <FiChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              className="hover:text-foreground cursor-pointer flex-shrink-0"
              onClick={() => setCurrentFolderId(null)}
            >
              Root
            </button>
            {breadcrumbs.map((folder) => (
              <React.Fragment key={folder.id}>
                <FiChevronRight className="w-3 h-3 flex-shrink-0" />
                <button
                  className={`cursor-pointer truncate ${folder.id === currentFolderId ? "text-foreground font-medium" : "hover:text-foreground"}`}
                  onClick={() => setCurrentFolderId(folder.id)}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-1 min-h-0" onClick={() => setSelectedAssetIds([])}>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 px-4 text-center">
              <FiUpload className="w-8 h-8 text-secondary-foreground/30" />
              <span className="text-xs text-secondary-foreground/60">
                {currentFolderId ? "This folder is empty" : "Drop files here or use the import button"}
              </span>
            </div>
          ) : (
            <div className="grid gap-y-1" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {items.map((item) => (
                <GridItem
                  key={item.id}
                  item={item}
                  onClick={handleSelect}
                  onDoubleClick={item.type === "folder" ? handleNavigateInto : handlePreview}

                  onRename={handleRename}
                  onDragStart={handleAssetDragStart}
                  editingId={editingId}
                  setEditingId={setEditingId}
                  isSelected={selectedAssetIds.includes(item.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Bottom bar: upload progress + zoom controls */}
        <div className="flex items-center gap-2 px-2 border-t flex-shrink-0" style={{ height: 28 }}>
          <div className="flex-1 min-w-0">
            {isUploading && (() => {
              const total = batchUpload?.total || 1;
              const completed = batchUpload?.completed || 0;
              const totalProgress = Math.round(((completed + uploadProgress / 100) / total) * 100);
              return (
                <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                  <div className="flex-1 bg-secondary rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-primary h-full transition-all"
                      style={{ width: `${totalProgress}%` }}
                    />
                  </div>
                  <span className="flex-shrink-0 tabular-nums">
                    {total > 1 ? `${completed + 1}/${total} · ` : ""}{totalProgress}%
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Drop zone overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-md flex items-center justify-center z-50 pointer-events-none">
          <span className="text-sm font-medium text-primary">
            Drop files to import
          </span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <Dialog open={showAssetsBrowser} onOpenChange={setShowAssetsBrowser}>
        {/* Backdrop clicks don't dismiss, matching the ModalContext modals —
            so the built-in close button is kept as the way out. */}
        <DialogContent
          className="max-w-2xl h-[720px] p-0 overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">Browse Assets</DialogTitle>
          <AssetsBrowser
            header="Add from Kagami"
            showUntagged={false}
            multiSelect
            onSelect={(assets) => handleBrowserSelect(assets)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
